/**
 * Application access control utilities
 */

// Map of route paths to application identifiers
export const APP_ROUTES = {
  DASHBOARD: '/suite/dashboard',
  DELIVERY: '/suite/delivery',
  SPECIAL_ORDERS: '/suite/specialorders',
  USER_MANAGEMENT: '/suite/user-management',
  SIMS: '/suite/sims',
  AUTO_SUB: '/suite/auto-sub',
  INVENTORY_MANAGEMENT: '/suite/inventory-management'
};

// Application ID mappings from backend
export const APP_IDS = {
  DELIVERY: 1,
  SPECIAL_ORDERS: 2,
  USER_MANAGEMENT: 3,
  SIMS: 6,
  AUTO_SUB: 7,
  INVENTORY_MANAGEMENT: 8
};

/**
 * Check if user has access to a specific application
 * @param {Object} userDetails - User details object from /user API
 * @param {string} appRoute - Application route path
 * @returns {boolean} - True if user has access
 */
export function hasAppAccess(userDetails, appRoute) {
  if (!userDetails || !userDetails.applicationIds) {
    return false;
  }

  const { applicationIds } = userDetails;

  // Map routes to application IDs from the API
  const routeAppIdMap = {
    [APP_ROUTES.DASHBOARD]: null, // Dashboard is accessible to all authenticated users
    [APP_ROUTES.DELIVERY]: APP_IDS.DELIVERY,
    [APP_ROUTES.SPECIAL_ORDERS]: APP_IDS.SPECIAL_ORDERS,
    [APP_ROUTES.USER_MANAGEMENT]: APP_IDS.USER_MANAGEMENT,
    [APP_ROUTES.SIMS]: APP_IDS.SIMS,
    [APP_ROUTES.AUTO_SUB]: APP_IDS.AUTO_SUB,
    [APP_ROUTES.INVENTORY_MANAGEMENT]: null
  };

  const requiredAppId = routeAppIdMap[appRoute];

  // Dashboard is accessible to all authenticated users
  if (requiredAppId === null) {
    return true;
  }

  // Check if the application ID is in the user's applicationIds array
  return applicationIds.includes(requiredAppId);
}

/**
 * Get list of accessible routes for the user
 * @param {Object} userDetails - User details object from /user API
 * @returns {Array} - Array of accessible route paths
 */
export function getAccessibleRoutes(userDetails) {
  if (!userDetails) {
    return [];
  }

  return Object.values(APP_ROUTES).filter(route => hasAppAccess(userDetails, route));
}
